import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  EXTENDED_SUPPORT_SERVICES,
  FINOPS_SEVERITY_RULES,
  type FinopsAlertSeverity,
} from '../../../../shared/types/finops';
import { RULE_LABEL_KEYS } from './alerts-format';

const DOT: Record<FinopsAlertSeverity, string> = {
  critical: 'bg-rose-500',
  warning: 'bg-amber-500',
  info: 'bg-slate-400',
};

const BADGE: Record<FinopsAlertSeverity, string> = {
  critical: 'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
  warning: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  info: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
};

export function SeverityDot({ severity }: { severity: FinopsAlertSeverity }) {
  return <span className={`inline-block h-2 w-2 rounded-full shrink-0 ${DOT[severity]}`} aria-hidden />;
}

export function AlertSeverityBadge({ severity }: { severity: FinopsAlertSeverity }) {
  const { t } = useTranslation();
  const label = {
    critical: t('finops.alerts.severity.critical'),
    warning: t('finops.alerts.severity.warning'),
    info: t('finops.alerts.severity.info'),
  }[severity];
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${BADGE[severity]}`}>
      <SeverityDot severity={severity} />
      {label}
    </span>
  );
}

/** How severity is decided — the classifier's rules from shared/types, first match wins, plus the `info` fallback. */
export function SeverityLegend() {
  const { t } = useTranslation();
  const services = EXTENDED_SUPPORT_SERVICES.join(', ');
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
          aria-label={t('finops.alerts.legend.title')}
        >
          <Info className="h-4 w-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80">
        <p className="text-sm font-semibold">{t('finops.alerts.legend.title')}</p>
        <p className="text-xs text-muted-foreground mt-0.5 mb-3">{t('finops.alerts.legend.intro')}</p>
        <ul className="space-y-2 text-xs">
          {FINOPS_SEVERITY_RULES.map((rule) => (
            <li key={rule.id} className="flex items-start gap-2">
              <span className="mt-1"><SeverityDot severity={rule.severity} /></span>
              <span>
                {RULE_LABEL_KEYS[rule.id] ? t(RULE_LABEL_KEYS[rule.id], { services }) : rule.label}
              </span>
            </li>
          ))}
          <li className="flex items-start gap-2">
            <span className="mt-1"><SeverityDot severity="info" /></span>
            <span>{t('finops.alerts.legend.fallback')}</span>
          </li>
        </ul>
      </PopoverContent>
    </Popover>
  );
}
